import styled from 'styled-components'
import { useRecoilValue } from 'recoil'
import { userInfoState } from '@/util/states'
import { useRouter } from 'next/router'
import { loadStripe } from '@stripe/stripe-js'
import { useABGroup } from 'src/hooks/useABGroup'

export const Paywall = ({ id, title, subtitle, author, thumbnail, url, price, endpoint = 'singleCheckout' }) => {
  const userInfo = useRecoilValue(userInfoState)
  const router = useRouter()
  const stripePromise = loadStripe(`${process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY}`)

  // A/B testing for exclusive post button - new custom dimension
  const exclusivePostButtonGroup = useABGroup('exclusive-post-button-test-group', ['a', 'b'])

  const getButtonText = () => {
    if (exclusivePostButtonGroup === 'b') {
      return 'Get Full Access'
    }
    return 'Unlock this post'
  }

  const handleCheckout = async () => {
    if (userInfo === 'pending') return
    if (!userInfo) {
      router.push({
        pathname: '/signup',
        query: { returnUrl: router.asPath }
      })
      return
    }

    // Track exclusive post button click with A/B test data
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'paywall_click', {
        method: 'exclusive_post_button',
        exclusive_post_button_group: exclusivePostButtonGroup || 'unknown',
        post_id: id,
        post_url: url,
        value: price || 1000,
        custom_parameter_1: 'exclusive_post_button_ab_test'
      });
    }

    const { sessionId } = await fetch(`/api/${endpoint}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify({
        pathname: router.pathname,
        postId: id,
        postUrl: url,
        title: title,
        userId: userInfo.uid,
        price: price || 1000
      })
    }).then((res) => res.json())

    const stripe = await stripePromise
    if (stripe) {
      const { error } = await stripe.redirectToCheckout({ sessionId })
      console.error('stripe returned an error: ', error)
    } else {
      console.error('the stripe object was null')
    }
  }

  return (
    <Container>
      <HeroImage src={thumbnail} alt={`${title}`} />
      <Content>
        <Title>{title}</Title>
        <Subtitle>{subtitle}</Subtitle>
        {author && <Author>{author}</Author>}
        <Button
          onClick={handleCheckout}
          data-ab-group={exclusivePostButtonGroup}
        >
          {getButtonText()}
        </Button>
      </Content>
    </Container>
  )
}

const Container = styled.div`
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: 100vh;
  background: white;

  @media (min-width: 1024px) {
    flex-direction: row-reverse;
    align-items: center;
    justify-content: center;
    gap: 3rem 
  }
`

const HeroImage = styled.img`
  max-width: 600px;
  height: auto;
  max-height: 80vh;
  object-fit: contain;
  object-position: center;
  border-radius: 8px;
  background: white;
  padding: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid #f0f0f0;

  @media (max-width: 768px) {
    max-width: 90vw;
    max-height: 60vh;
    margin: 2rem 1rem 1.5rem 1rem;
    padding: 15px;
  }
`

const Content = styled.div`
  text-align: center;
  padding: 0 2rem;
  margin-top: 1.5rem;

  @media (max-width: 768px) {
    padding: 0 1rem;
    margin-top: 1rem;
  }
`

const Title = styled.h2`
  margin: 0 0 1rem 0;
  line-height: 1.2;
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 1.75rem;
  font-weight: 300;

  @media (min-width: 1024px) {
    font-size: 2rem;
  }
`

const Subtitle = styled.h6`
  margin: 0 0 0.75rem 0;
  color: #666;
  line-height: 1.4;
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 1rem;
  font-weight: 300;

  @media (min-width: 1024px) {
    font-size: 1.125rem;
  }
`

const Author = styled.h6`
  margin: 0 0 1rem 0;
  color: #888;
  line-height: 1.4;
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 0.875rem;
  font-weight: 300;
  opacity: 0.75;

  @media (min-width: 1024px) {
    font-size: 1rem;
  }
`

const Button = styled.button`
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #556B2F;
  color: white;
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  line-height: 1.2;
  display: inline-block;
  vertical-align: middle;

  @media (min-width: 1024px) {
    font-size: 1.125rem;
    padding: 0.875rem 2rem;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    background-color: #4A5D28;
  }
`
